<template>
  <section class="section admin-credit-refill-plans">
    <div class="container">
      <div class="columns">
        <div class="column">
          <div class="columns is-desktop">
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model.number="perPage" @change="onPerPageChange">
                      <option :value="10">10 per page</option>
                      <option :value="15">15 per page</option>
                      <option :value="25">25 per page</option>
                      <option :value="50">50 per page</option>
                      <option :value="100">100 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-1">
              <div class="field">
                <div class="control">
                  <input
                    v-model="filterUserId"
                    type="text"
                    class="input"
                    placeholder="User ID"
                    @keyup.enter="onFilter"
                  >
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <div class="control">
                  <input
                    v-model="filterUserEmail"
                    type="text"
                    class="input"
                    placeholder="User email"
                    @keyup.enter="onFilter"
                  >
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="filterCadence" @change="onFilter">
                      <option value="">Any cadence</option>
                      <option value="1">Every month</option>
                      <option value="2">Every 2 months</option>
                      <option value="3">Every 3 months</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="filterStatus" @change="onFilter">
                      <option value="">Any status</option>
                      <option value="active">Active</option>
                      <option value="payment_failed">Payment failed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column is-1">
              <button class="button is-info is-fullwidth" @click="onFilter">Search</button>
            </div>
            <div class="column is-2">
              <button
                class="button is-link is-fullwidth"
                :class="{ 'is-loading': isExportingPlans }"
                :disabled="isExportingPlans || isExportingTransactions"
                @click="exportPlans"
              >
                Export Plans
              </button>
            </div>
            <div class="column is-2">
              <button
                class="button is-link is-fullwidth"
                :class="{ 'is-loading': isExportingTransactions }"
                :disabled="isExportingPlans || isExportingTransactions"
                @click="exportTransactions"
              >
                Export Transactions
              </button>
            </div>
          </div>

          <b-table
            ref="plansTable"
            :data="records"
            :loading="isLoading"
            paginated
            backend-pagination
            :total="totalRows"
            :per-page="perPage"
            :current-page="currentPage"
            @page-change="onPageChange"
            backend-sorting
            :default-sort-direction="defaultSortOrder"
            :default-sort="[sortField, sortOrder]"
            @sort="onSort"
            detailed
            detail-key="id"
            @click="toggleRowDetails"
          >
            <template slot-scope="props">
              <b-table-column field="id" label="Plan ID" sortable numeric>{{ props.row.id }}</b-table-column>
              <b-table-column field="userId" label="User ID" numeric>{{ props.row.userId }}</b-table-column>
              <b-table-column field="userEmail" label="Email" sortable>{{ props.row.userEmail }}</b-table-column>
              <b-table-column field="accountType" label="Package">{{ serviceLabel(props.row.accountType) }}</b-table-column>
              <b-table-column field="userJoinedAt" label="Joined">{{ formatDate(props.row.userJoinedAt) }}</b-table-column>
              <b-table-column field="purchasedCreditsBalance" label="Credit Balance" numeric>{{ props.row.purchasedCreditsBalance }}</b-table-column>
              <b-table-column field="credits" label="Credits / Refill" numeric>{{ props.row.credits }}</b-table-column>
              <b-table-column field="intervalMonths" label="Cadence">Every {{ props.row.intervalMonths }} month{{ props.row.intervalMonths > 1 ? 's' : '' }}</b-table-column>
              <b-table-column field="chargeAmount" label="Charge ($)" numeric>{{ props.row.chargeAmount | round(2) }}</b-table-column>
              <b-table-column field="status" label="Status">
                <span :class="planStatusClass(props.row.status)">{{ _.startCase(props.row.status) }}</span>
              </b-table-column>
              <b-table-column field="nextChargeDate" label="Next Charge" sortable>{{ formatDate(props.row.nextChargeDate) }}</b-table-column>
              <b-table-column field="createdAt" label="Created" sortable>{{ formatDate(props.row.createdAt) }}</b-table-column>
            </template>
            <template slot="detail" slot-scope="props">
              <div @click.stop>
                <div v-if="!props.row.refills || props.row.refills.length === 0">
                  <i>No transactions yet</i>
                </div>
                <table v-else class="table is-fullwidth">
                  <thead>
                    <tr>
                      <th>Refill ID</th>
                      <th>Status</th>
                      <th>Credits</th>
                      <th>Charge ($)</th>
                      <th>Cycle Date</th>
                      <th>Paid At</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="refill in props.row.refills" :key="refill.id">
                      <td>{{ refill.id }}</td>
                      <td>
                        <span :class="refillStatusClass(refill.status)">{{ _.startCase(refill.status) }}</span>
                      </td>
                      <td>{{ refill.credits }}</td>
                      <td>{{ refill.chargeAmount | round(2) }}</td>
                      <td>{{ formatDate(refill.cycleDate) }}</td>
                      <td>{{ formatDate(refill.paidAt) }}</td>
                      <td>{{ formatDate(refill.createdAt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import _ from 'lodash'
import { toLocalDateString } from '@/utils'
import { mixin as ServiceMixin } from '@/components/service_mixin'
import { getMainAPIClient } from '@/api/openapi'

async function getAdminCreditRefillPlans (params = {}) {
  return (await getMainAPIClient()).credit_auto_refill_admin_list(params)
}

async function exportAdminCreditRefillPlans (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/credit-auto-refill-plans/export/',
    { params, responseType: 'blob' },
  )
}

async function exportAdminCreditRefillTransactions (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/credit-auto-refill-plans/export-transactions/',
    { params, responseType: 'blob' },
  )
}

export default {
  name: 'AdminCreditRefillPlans',
  mixins: [ServiceMixin],
  data () {
    return {
      records: [],
      totalRows: 0,
      isLoading: false,
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'asc',
      currentPage: 1,
      perPage: 15,
      filterUserId: '',
      filterUserEmail: '',
      filterCadence: '',
      filterStatus: '',
      isExportingPlans: false,
      isExportingTransactions: false,
    }
  },
  computed: {
    _ () {
      return _
    },
  },
  methods: {
    formatDate (dateStr) {
      return dateStr ? toLocalDateString(dateStr) : '-'
    },
    planStatusClass (status) {
      return {
        'has-text-success': status === 'active',
        'has-text-danger': status === 'payment_failed',
        'has-text-grey': status === 'cancelled',
      }
    },
    refillStatusClass (status) {
      return {
        'has-text-success': status === 'paid',
        'has-text-info': status === 'pending',
        'has-text-danger': status === 'failed',
        'has-text-grey': status === 'voided' || status === 'expired',
      }
    },
    getRequestParams () {
      return {
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        page: this.currentPage,
        perPage: this.perPage,
        userId: this.filterUserId.trim(),
        userEmail: this.filterUserEmail.trim(),
        cadence: this.filterCadence,
        status: this.filterStatus,
      }
    },
    getExportFileName (report) {
      const now = new Date()
      const date = `${now.getUTCFullYear()}_${now.getUTCMonth() + 1}_${now.getUTCDate()}`
      return `CreditAutoRefill${report}_${date}.csv`
    },
    downloadCsvBlob (blob, fileName) {
      const downloadLink = document.createElement('a')
      const url = window.URL.createObjectURL(blob)
      downloadLink.download = fileName
      downloadLink.href = url
      downloadLink.style.display = 'none'
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(url)
    },
    exportPlans () {
      this.isExportingPlans = true
      this.$store.commit('showLoading')
      return exportAdminCreditRefillPlans(this.getRequestParams())
        .then(({ data }) => {
          this.downloadCsvBlob(data, this.getExportFileName('Plans'))
        })
        .finally(() => {
          this.isExportingPlans = false
          this.$store.commit('hideLoading')
        })
    },
    exportTransactions () {
      this.isExportingTransactions = true
      this.$store.commit('showLoading')
      return exportAdminCreditRefillTransactions(this.getRequestParams())
        .then(({ data }) => {
          this.downloadCsvBlob(data, this.getExportFileName('Transactions'))
        })
        .finally(() => {
          this.isExportingTransactions = false
          this.$store.commit('hideLoading')
        })
    },
    onPerPageChange () {
      this.currentPage = 1
      this.loadData()
    },
    onPageChange (page) {
      this.currentPage = page
      this.loadData()
    },
    onSort (field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadData()
    },
    onFilter () {
      this.currentPage = 1
      this.loadData()
    },
    toggleRowDetails (row) {
      if (this.$refs.plansTable && row) {
        this.$refs.plansTable.toggleDetails(row)
      }
    },
    loadData () {
      this.isLoading = true
      this.$store.commit('showLoading')
      return getAdminCreditRefillPlans(this.getRequestParams())
        .then(({ data }) => {
          this.totalRows = data.total || 0
          this.records = data.records || []
        })
        .finally(() => {
          this.isLoading = false
          this.$store.commit('hideLoading')
        })
    },
  },
  beforeMount () {
    this.loadData()
  },
}
</script>

<style lang="scss" scoped>
.admin-credit-refill-plans ::v-deep .table tr {
  cursor: pointer;
}
</style>
